import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentTrackingTab = ({ sessionData, onUpdatePayment }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedPayments, setSelectedPayments] = useState([]);

  const paymentFilterOptions = [
    { value: 'all', label: '전체' },
    { value: 'paid', label: '결제 완료' },
    { value: 'pending', label: '결제 대기' },
    { value: 'overdue', label: '결제 기한 초과' }
  ];

  const filteredParticipants = sessionData?.participants?.filter(participant => {
    const matchesSearch = 
      participant?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      participant?.phone?.includes(searchQuery);
    
    const matchesPayment = paymentFilter === 'all' || participant?.payment === paymentFilter;

    return matchesSearch && matchesPayment;
  });

  const handlePaymentConfirm = (participantId) => {
    const participant = sessionData?.participants?.find(p => p?.id === participantId);
    if (participant) {
      onUpdatePayment({ ...participant, payment: 'paid' });
    }
  };

  const handlePaymentCancel = (participantId) => {
    const participant = sessionData?.participants?.find(p => p?.id === participantId);
    if (participant) {
      onUpdatePayment({ ...participant, payment: 'pending' });
    }
  };

  const handleBulkPaymentConfirm = () => {
    if (selectedPayments?.length === 0) {
      alert('참가자를 선택해주세요.');
      return;
    }
    
    if (window.confirm(`선택한 ${selectedPayments?.length}명의 결제를 확인하시겠습니까?`)) {
      selectedPayments?.forEach(id => {
        const participant = sessionData?.participants?.find(p => p?.id === id);
        if (participant) {
          onUpdatePayment({ ...participant, payment: 'paid' });
        }
      });
      setSelectedPayments([]);
    }
  };

  const handleBulkPaymentCancel = () => {
    if (selectedPayments?.length === 0) {
      alert('참가자를 선택해주세요.');
      return;
    }

    if (window.confirm(`선택한 ${selectedPayments?.length}명의 결제를 취소(대기) 처리하시겠습니까?`)) {
      selectedPayments?.forEach(id => {
        const participant = sessionData?.participants?.find(p => p?.id === id);
        if (participant) {
          onUpdatePayment({ ...participant, payment: 'pending' });
        }
      });
      setSelectedPayments([]);
    }
  };

  const togglePaymentSelection = (participantId) => {
    setSelectedPayments(prev => {
      if (prev?.includes(participantId)) {
        return prev?.filter(id => id !== participantId);
      }
      return [...prev, participantId];
    });
  };

  const totalAmount = sessionData?.participants?.reduce((sum, p) => sum + (p?.amount || 0), 0);
  const paidAmount = sessionData?.participants?.filter(p => p?.payment === 'paid')?.reduce((sum, p) => sum + (p?.amount || 0), 0);
  const pendingAmount = totalAmount - paidAmount;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md flex items-center justify-center">
              <Icon name="DollarSign" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">총 금액</p>
              <p className="text-xl md:text-2xl font-heading font-bold text-foreground">
                ₩{totalAmount?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-md flex items-center justify-center">
              <Icon name="CheckCircle" size={24} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">수금 완료</p>
              <p className="text-xl md:text-2xl font-heading font-bold text-success">
                ₩{paidAmount?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 bg-card rounded-md border border-border">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-warning/10 rounded-md flex items-center justify-center">
              <Icon name="Clock" size={24} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm caption text-muted-foreground">미수금</p>
              <p className="text-xl md:text-2xl font-heading font-bold text-warning">
                ₩{pendingAmount?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6 bg-card rounded-md border border-border">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Building2" size={20} color="var(--color-primary)" />
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
            입금 계좌 정보
          </h3>
        </div>
        
        <div className="space-y-3 p-4 bg-muted rounded-md">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground min-w-24">은행명:</span>
            <span className="text-sm md:text-base font-medium text-foreground">
              {sessionData?.bankInfo?.bankName}
            </span>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground min-w-24">계좌번호:</span>
            <span className="text-sm md:text-base font-medium data-text text-foreground">
              {sessionData?.bankInfo?.accountNumber}
            </span>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground min-w-24">예금주:</span>
            <span className="text-sm md:text-base font-medium text-foreground">
              {sessionData?.bankInfo?.accountHolder}
            </span>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground min-w-24">입금 기한:</span>
            <span className="text-sm md:text-base font-medium text-error">
              {new Date(sessionData.paymentDeadline)?.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6 bg-card rounded-md border border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
            결제 현황
          </h3>

          {selectedPayments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="success"
                size="default"
                iconName="CheckCircle"
                iconPosition="left"
                onClick={handleBulkPaymentConfirm}
              >
                결제 확인 ({selectedPayments?.length})
              </Button>
              <Button
                variant="outline"
                size="default"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleBulkPaymentCancel}
              >
                결제 취소 ({selectedPayments?.length})
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="이름 또는 전화번호로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>
          
          <Select
            options={paymentFilterOptions}
            value={paymentFilter}
            onChange={setPaymentFilter}
            placeholder="결제 상태 필터"
            className="w-full lg:w-48"
          />
        </div>

        <div className="space-y-3">
          {filteredParticipants?.map((participant) => {
            const isSelected = selectedPayments?.includes(participant?.id);
            const paymentConfig = {
              paid: { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle' },
              pending: { color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
              overdue: { color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle' }
            }?.[participant?.payment];

            return (
              <div
                key={participant?.id}
                className={`
                  p-4 rounded-md border transition-athletic
                  ${isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card'}
                `}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => togglePaymentSelection(participant?.id)}
                    className="mt-1 w-5 h-5 rounded border-border"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                      <div>
                        <h4 className="font-medium text-foreground">{participant?.name}</h4>
                        <p className="text-sm caption text-muted-foreground">{participant?.phone}</p>
                      </div>
                      
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${paymentConfig?.bg}`}>
                        <Icon name={paymentConfig?.icon} size={14} />
                        <span className={`text-sm font-medium ${paymentConfig?.color}`}>
                          {participant?.payment === 'paid' ? '결제 완료' : 
                           participant?.payment === 'pending' ? '결제 대기' : '기한 초과'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <span className="text-sm caption text-muted-foreground">금액:</span>
                        <span className="text-base md:text-lg font-heading font-bold text-foreground">
                          ₩{participant?.amount?.toLocaleString()}
                        </span>
                      </div>

                      {participant?.payment !== 'paid' ? (
                        <Button
                          variant="success"
                          size="sm"
                          iconName="CheckCircle"
                          iconPosition="left"
                          onClick={() => handlePaymentConfirm(participant?.id)}
                        >
                          결제 확인
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="RotateCcw"
                          iconPosition="left"
                          onClick={() => handlePaymentCancel(participant?.id)}
                        >
                          결제 취소
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredParticipants?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">검색 결과가 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentTrackingTab;